import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send, Paperclip, X, Download, FileText, ArrowLeft, ShieldCheck, RotateCcw, Building2 } from "lucide-react";
import {
  STATUS_META, PRIORITY_META, fmtDateTime, fmtSize, uploadSupportFile, supportFileUrl, fmtTicketNo,
} from "./supportUtils";

export const SupportConversation = ({ ticketId, isSuper = false, onBack, onChanged }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [atts, setAtts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/support/tickets/${ticketId}`);
      setTicket(r.data);
      onChanged?.();
    } catch { toast.error("Impossibile caricare il ticket"); }
    finally { setLoading(false); }
  }, [ticketId, onChanged]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [ticket?.messages?.length]);

  const onAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: troppo grande (max 10 MB)`); continue; }
      try { const rec = await uploadSupportFile(f); setAtts((p) => [...p, rec]); }
      catch { toast.error(`Caricamento di ${f.name} non riuscito`); }
    }
    setUploading(false);
  };

  const removeAtt = (id) => setAtts((p) => p.filter((a) => a.file_id !== id));

  const send = async () => {
    if (!body.trim() && !atts.length) return;
    setSending(true);
    try {
      const r = await api.post(`/support/tickets/${ticketId}/messages`, { body: body.trim(), attachments: atts });
      setTicket(r.data.ticket);
      setBody(""); setAtts([]);
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Invio non riuscito"); }
    finally { setSending(false); }
  };

  const changeStatus = async (status) => {
    setSavingStatus(true);
    try {
      await api.put(`/admin/support/tickets/${ticketId}/status`, { status });
      const closed = ["risolto", "chiuso"].includes(status);
      const myRole = isSuper ? "super_admin" : "admin";
      setTicket((t) => ({
        ...t, status, closed,
        can_reply: closed ? false : (t.last_author_role ? t.last_author_role !== myRole : true),
      }));
      toast.success(status === "aperto" || status === "in_lavorazione" ? "Ticket riaperto" : "Stato aggiornato");
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Aggiornamento non riuscito"); }
    finally { setSavingStatus(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!ticket) return null;

  const st = STATUS_META[ticket.status] || STATUS_META.aperto;
  const pr = PRIORITY_META[ticket.priority] || PRIORITY_META.media;
  const canReply = ticket.can_reply !== false;
  const waitingFor = isSuper ? "dell'amministratore" : "dell'Assistenza Sedio";

  return (
    <div className="flex flex-col h-full" data-testid="support-conversation">
      <div className="flex items-start gap-3 pb-4 border-b border-border">
        {onBack && (
          <button onClick={onBack} data-testid="support-back" className="mt-0.5 text-muted-foreground hover:text-foreground"><ArrowLeft className="h-5 w-5" /></button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {ticket.ticket_no != null && (
              <span data-testid="support-ticket-no" className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-slate-800 text-white">{fmtTicketNo(ticket.ticket_no)}</span>
            )}
            <h3 className="font-semibold text-base truncate">{ticket.subject}</h3>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`} data-testid="support-status-badge">{st.label}</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${pr.cls}`}>Priorità: {pr.label}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {ticket.category} · aperto il {fmtDateTime(ticket.created_at)}
          </div>
          {isSuper && ticket.studio && (
            <div className="mt-2 rounded-lg border border-border bg-secondary/50 p-2.5 text-xs" data-testid="support-studio-info">
              <div className="flex items-center gap-1.5 font-semibold text-foreground">
                <Building2 className="h-3.5 w-3.5 text-[#007BFF] shrink-0" />
                {ticket.studio.ragione_sociale || ticket.admin_name || ticket.admin_email || "Studio senza nome"}
              </div>
              <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-muted-foreground">
                {ticket.admin_email && <div>Email: <span className="text-foreground/90">{ticket.admin_email}</span></div>}
                {ticket.studio.referente && <div>Referente: <span className="text-foreground/90">{ticket.studio.referente}</span></div>}
                {ticket.studio.partita_iva && <div>P.IVA: <span className="text-foreground/90">{ticket.studio.partita_iva}</span></div>}
                {ticket.studio.telefono && <div>Tel: <span className="text-foreground/90">{ticket.studio.telefono}</span></div>}
                {(ticket.studio.citta || ticket.studio.provincia) && <div>Città: <span className="text-foreground/90">{[ticket.studio.citta, ticket.studio.provincia && `(${ticket.studio.provincia})`].filter(Boolean).join(" ")}</span></div>}
                {ticket.studio.pec && <div>PEC: <span className="text-foreground/90">{ticket.studio.pec}</span></div>}
              </div>
            </div>
          )}
        </div>
        {isSuper && (
          <div className="shrink-0" data-testid="support-status-select">
            <Select value={ticket.status} onValueChange={changeStatus} disabled={savingStatus}>
              <SelectTrigger className="h-8 w-[160px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k} className="text-xs" data-testid={`support-status-opt-${k}`}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto py-4 space-y-4 min-h-[240px] max-h-[50vh]">
        {(ticket.messages || []).map((m) => {
          const mine = isSuper ? m.author_role === "super_admin" : m.author_role === "admin";
          const fromSuper = m.author_role === "super_admin";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`support-msg-${m.id}`}>
              <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#007BFF] text-white" : "bg-secondary text-foreground"}`}>
                <div className={`text-[10px] font-semibold mb-1 flex items-center gap-1 ${mine ? "text-white/80" : "text-muted-foreground"}`}>
                  {fromSuper && <ShieldCheck className="h-3 w-3" />}
                  {fromSuper ? "Assistenza Sedio" : (m.author_name || "Amministratore")} · {fmtDateTime(m.created_at)}
                </div>
                {m.body && <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>}
                {(m.attachments || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.attachments.map((a) => (
                      <a key={a.file_id} href={supportFileUrl(a.file_id)} target="_blank" rel="noopener noreferrer" download
                        data-testid={`support-att-${a.file_id}`}
                        className={`inline-flex items-center gap-1.5 text-[11px] rounded-lg px-2 py-1 ${mine ? "bg-white/20 hover:bg-white/30 text-white" : "bg-background border border-border hover:bg-secondary"}`}>
                        <FileText className="h-3 w-3 shrink-0" />
                        <span className="truncate max-w-[140px]">{a.filename}</span>
                        {a.size ? <span className="opacity-70">· {fmtSize(a.size)}</span> : null}
                        <Download className="h-3 w-3 shrink-0" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="pt-3 border-t border-border">
        {ticket.closed ? (
          <div data-testid="support-closed-note" className="mb-2 flex items-center justify-between gap-3 text-xs text-muted-foreground bg-secondary/60 border border-border rounded-lg px-3 py-2">
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
              Questo ticket è {ticket.status === "chiuso" ? "chiuso" : "risolto"}: non è più possibile inviare messaggi.
            </span>
            {isSuper && (
              <Button data-testid="support-reopen-btn" size="sm" variant="outline" disabled={savingStatus}
                onClick={() => changeStatus("in_lavorazione")} className="rounded-md shrink-0 h-7">
                {savingStatus ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}Riapri ticket
              </Button>
            )}
          </div>
        ) : !canReply && (
          <div data-testid="support-wait-turn" className="mb-2 flex items-center gap-2 text-xs text-muted-foreground bg-secondary/60 border border-border rounded-lg px-3 py-2">
            <Loader2 className="h-3.5 w-3.5 shrink-0" />
            Hai già inviato un messaggio. Attendi la risposta {waitingFor} prima di scriverne un altro.
          </div>
        )}
        {atts.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2" data-testid="support-reply-atts">
            {atts.map((a) => (
              <span key={a.file_id} className="inline-flex items-center gap-1.5 text-xs bg-[#007BFF]/10 text-[#0056B3] border border-[#007BFF]/30 rounded-lg px-2 py-1">
                <Paperclip className="h-3 w-3" /><span className="truncate max-w-[140px]">{a.filename}</span>
                <button onClick={() => removeAtt(a.file_id)} className="hover:bg-[#007BFF]/20 rounded-full p-0.5"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}
        <Textarea data-testid="support-reply-input" value={body} onChange={(e) => setBody(e.target.value)} disabled={!canReply}
          placeholder={canReply ? "Scrivi un messaggio…" : "In attesa della risposta della controparte…"} className="min-h-[70px] text-sm disabled:opacity-60" />
        <div className="flex items-center gap-2 mt-2">
          <input ref={fileRef} type="file" multiple className="hidden" data-testid="support-reply-file" onChange={onAttach} />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading || !canReply} className="rounded-md">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </Button>
          <Button data-testid="support-send-btn" onClick={send} disabled={sending || !canReply || (!body.trim() && !atts.length)} className="bg-[#007BFF] hover:bg-[#0056B3] rounded-md ml-auto">
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Invia
          </Button>
        </div>
      </div>
    </div>
  );
};
